'use client'

import React, { type ChangeEventHandler, useId } from 'react'
import { clsx } from 'clsx'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import { InputBase, type InputBaseProps } from 'ui/src/components/InputBase'
import { ChevronIcon } from 'ui/src/icons/Chevron'
import { type LegacyUIColorKey } from 'ui/src/theme/colors.types'
import { tokens } from 'ui/src/theme/tokens.css'
import { useHighlightAnimation } from 'ui/src/utils/useHighlightAnimation'
import {
  chevronIcon,
  inputLabel,
  select,
  vars,
  wrapper,
  wrapperVariants,
  wrapperWithLabelVariants,
} from './InputSelect.css'

export type InputSelectProps = InputBaseProps & {
  name: string
  options: ReadonlyArray<{ name: string; value: string; disabled?: boolean }>
  value?: string
  defaultValue?: string
  onChange?: React.ChangeEventHandler<HTMLSelectElement>
  required?: boolean
  disabled?: boolean
  placeholder?: string
  autoFocus?: boolean
  className?: string
  size?: 'small' | 'medium' | 'large'
  backgroundColor?: LegacyUIColorKey
  highlightOnSelect?: boolean
}

export const InputSelect = ({
  options,
  name,
  onChange,
  value,
  defaultValue,
  placeholder,
  label,
  size = 'medium',
  className,
  backgroundColor: _backgroundColor,
  highlightOnSelect = true,
  ...rest
}: InputSelectProps) => {
  const backgroundColor = _backgroundColor ? tokens.legacyColors[_backgroundColor] : undefined

  const { highlight, animationProps } = useHighlightAnimation<HTMLSelectElement>()

  const handleChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
    onChange?.(event)
    if (highlightOnSelect) {
      highlight()
    }
  }

  const labelId = useId()
  const selectId = useId()

  return (
    <InputBase {...rest}>
      {() => (
        <div className={clsx(wrapper, wrapperVariants[size], label && wrapperWithLabelVariants[size], className)}>
          {label && (
            <label id={labelId} htmlFor={selectId} className={inputLabel}>
              {label}
            </label>
          )}
          <select
            id={selectId}
            name={name}
            onChange={handleChange}
            value={value}
            defaultValue={value != null ? undefined : (defaultValue ?? '')}
            style={backgroundColor ? assignInlineVars({ [vars.inputBackground]: backgroundColor }) : undefined}
            className={select}
            {...animationProps}
            {...rest}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map(({ name, value, disabled }) => (
              <option key={value} value={value} disabled={disabled}>
                {name}
              </option>
            ))}
          </select>

          <ChevronIcon size="1rem" className={chevronIcon} />
        </div>
      )}
    </InputBase>
  )
}
